import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import SearchIcon from '@mui/icons-material/Search';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useProductosMaterialContext } from 'context';
import { useApi } from 'lib/api';
import { FP, PageHeader, fpInputSx, fpDataGridSx } from 'components/financepro';

const btnBase = { textTransform: 'none', borderRadius: 1.8, fontWeight: 700, boxShadow: 'none' };
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: '#FFFFFF',
  border: `1px solid ${c}`,
  '&:hover': { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
  '&.Mui-disabled': { color: FP.color.textMuted, borderColor: FP.color.border },
});
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: '#FFFFFF',
  '&:hover': { bgcolor: hover, boxShadow: 'none', color: '#FFFFFF' },
  '&.Mui-disabled': { bgcolor: `${bg}66`, color: '#FFFFFF' },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const fmtMoney = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v || '').replace(/,/g, ''));
  if (Number.isNaN(n)) return '0.00';
  return n.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const hoyISO = () => new Date().toISOString().slice(0, 10);

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: '100%', py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin préstamos registrados
      </Typography>
    </Stack>
  );
}

const statusCell = (params) => {
  const v = params.value;
  const label = v === 'A' ? 'Activo' : v === 'P' ? 'Pagado' : v;
  const color = v === 'A' ? '#2E7D32' : '#1565C0';
  const bg = v === 'A' ? '#E8F5E9' : '#E3F2FD';
  return (
    <Box
      sx={{
        px: 1.2, py: 0.3, borderRadius: 1, fontWeight: 700,
        fontSize: '0.75rem', bgcolor: bg, color, display: 'inline-block',
      }}
    >
      {label}
    </Box>
  );
};

const moneyCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  align: 'right',
  headerAlign: 'right',
  valueFormatter: (value) => fmtMoney(value),
});

const columnsIndividual = [
  { field: 'id', headerName: 'No. Prest', width: 90 },
  { field: 'fecha', headerName: 'Fecha', width: 110 },
  moneyCol('solicitado', 'Solicitado'),
  moneyCol('pagado', 'Pagado'),
  moneyCol('balance', 'Balance'),
  { field: 'cuotasapagar', headerName: 'Cuotas', width: 80, align: 'right', headerAlign: 'right' },
  { field: 'estatus', headerName: 'Status', width: 90, renderCell: statusCell },
  { field: 'motivo', headerName: 'Motivo', flex: 1, minWidth: 160 },
];

const columnsCartera = [
  { field: 'num_emp', headerName: 'Emp #', width: 70 },
  { field: 'nombres', headerName: 'Empleado', flex: 1, minWidth: 180 },
  { field: 'id', headerName: 'No. Prest', width: 90 },
  { field: 'fecha', headerName: 'Fecha', width: 110 },
  moneyCol('solicitado', 'Solicitado'),
  moneyCol('pagado', 'Pagado'),
  moneyCol('balance', 'Balance'),
  { field: 'cuotasapagar', headerName: 'Cuotas', width: 75, align: 'right', headerAlign: 'right' },
  { field: 'estatus', headerName: 'Status', width: 90, renderCell: statusCell },
  { field: 'motivo', headerName: 'Motivo', minWidth: 140 },
];

const EstadoPrestamos = ({ onVolver }) => {
  const { controller, selectedCompany, showError } = useProductosMaterialContext();
  const { usuarioLog, nombreNegocio } = controller;
  const { post } = useApi();

  const puedeOperar = Boolean(selectedCompany?.db_empresa && usuarioLog);

  const [empleados, setEmpleados] = useState([]);
  const [empleadoSel, setEmpleadoSel] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(hoyISO());
  const [cargando, setCargando] = useState(false);
  const [procesado, setProcesado] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [esCartera, setEsCartera] = useState(false);

  const tablaRef = useRef(null);

  const credencialesEmpresa = useCallback(
    () => ({ id_empresa: selectedCompany }),
    [selectedCompany],
  );

  useEffect(() => {
    if (!puedeOperar) return;
    (async () => {
      try {
        const { ok, data } = await post('/api/nomina_listarempleados', credencialesEmpresa());
        if (ok && Array.isArray(data.empleados)) setEmpleados(data.empleados);
      } catch { /* silencioso */ }
    })();
  }, [puedeOperar, post, credencialesEmpresa]);

  const rows = useMemo(
    () => (resultado?.prestamos || []),
    [resultado],
  );

  const procesar = async () => {
    setCargando(true);
    setProcesado(false);
    try {
      const body = { ...credencialesEmpresa(), fecha_hasta: fechaHasta };
      if (empleadoSel) {
        body.numemp = String(typeof empleadoSel === 'object' ? empleadoSel.numemp : empleadoSel);
      }
      const { ok, data } = await post('/api/nomina_prestamo_estado', body);
      if (!ok) throw new Error(data.Error || 'Error al consultar estado de cuenta');
      setResultado(data);
      setEsCartera(!empleadoSel);
      setProcesado(true);
    } catch (e) {
      showError(e.message || 'Error al consultar estado de cuenta');
    } finally {
      setCargando(false);
    }
  };

  const imprimir = () => {
    if (!tablaRef.current) return;
    const contenido = tablaRef.current.innerHTML;
    const ventana = window.open('', '_blank', 'width=900,height=700');
    ventana.document.write(`
      <html>
        <head>
          <title>Estado de Cuenta</title>
          <style>
            body { font-family: Arial, sans-serif; font-size: 11px; margin: 20px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { padding: 4px 6px; border-bottom: 1px solid #ccc; }
            th { background: #eee; font-weight: bold; }
            td.right, th.right { text-align: right; }
            td.left, th.left { text-align: left; }
            .encabezado { text-align: center; font-weight: bold; margin-bottom: 4px; }
            .total td { font-weight: bold; border-top: 2px solid #333; }
            .firma { margin-top: 60px; }
            .firma td { border: none; }
            @media print { body { margin: 0; } }
            @page { size: ${esCartera ? 'landscape' : 'portrait'}; margin: 12mm; }
          </style>
        </head>
        <body>${contenido}</body>
      </html>
    `);
    ventana.document.close();
    ventana.focus();
    setTimeout(() => ventana.print(), 400);
  };

  const renderTablaImpresion = () => {
    if (!procesado || !rows.length) return null;
    const fmt = (v) => fmtMoney(v);
    const titulo = esCartera
      ? 'Cartera General de Préstamos'
      : 'Estado General / Balance Empleado';
    const subtitulo = esCartera
      ? ''
      : `${resultado.empleado?.numemp} - ${resultado.empleado?.nombres}`;

    return (
      <div>
        <div className="encabezado">{resultado.nombre_empresa}</div>
        <div className="encabezado">{titulo}</div>
        {subtitulo && <div className="encabezado">{subtitulo}</div>}
        <div className="encabezado">AL {fechaHasta}</div>
        <br /><br />
        <table>
          <thead>
            <tr>
              {esCartera && <th className="left">Emp #</th>}
              {esCartera && <th className="left">Empleado</th>}
              <th className="left">No. Prest</th>
              <th className="left">FECHA</th>
              <th className="right">SOLICITADO</th>
              <th className="right">PAGADO</th>
              <th className="right">BALANCE</th>
              <th className="right">STATUS</th>
              <th className="left" style={{ paddingLeft: 12 }}>MOTIVO</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                {esCartera && <td className="left">{r.num_emp}</td>}
                {esCartera && <td className="left">{r.nombres}</td>}
                <td className="left">{r.id}</td>
                <td className="left">{r.fecha}</td>
                <td className="right">{fmt(r.solicitado)}</td>
                <td className="right">{fmt(r.pagado)}</td>
                <td className="right">{fmt(r.balance)}</td>
                <td className="right">{r.estatus === 'A' ? 'Activo' : 'Pagado'}</td>
                <td className="left" style={{ paddingLeft: 12 }}>{r.motivo}</td>
              </tr>
            ))}
            <tr className="total">
              {esCartera && <td />}
              {esCartera && <td />}
              <td className="left">TOTALES</td>
              <td />
              <td />
              <td />
              <td className="right">{fmt(resultado.total_balance)}</td>
              <td />
              <td />
            </tr>
          </tbody>
        </table>
        <table className="firma">
          <tbody>
            <tr><td>______________________</td></tr>
            <tr><td>Preparado Por:</td></tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Estado de Cuenta"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Balance de préstamos`
            : 'Balance de préstamos'
        }
        cta={
          onVolver ? (
            <Button startIcon={<ArrowBackOutlinedIcon />} onClick={onVolver} sx={btnOutline(FP.color.navy)}>
              Volver
            </Button>
          ) : null
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                type="date"
                label="Al"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={empleados}
                value={empleadoSel}
                onChange={(_, v) => setEmpleadoSel(v)}
                getOptionLabel={(o) => o?.label || ''}
                isOptionEqualToValue={(a, b) => String(a?.numemp) === String(b?.numemp)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empleado"
                    placeholder="En blanco = cartera completa"
                    InputLabelProps={{ shrink: true }}
                    sx={fpInputSx}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Button
                startIcon={cargando ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
                onClick={procesar}
                disabled={cargando || !puedeOperar}
                sx={btnSolid(FP.color.navy, FP.color.navyDeep)}
              >
                Procesar
              </Button>
            </Grid>
            {procesado && rows.length > 0 && (
              <Grid item xs={12} sm={2}>
                <Button
                  startIcon={<PrintOutlinedIcon />}
                  onClick={imprimir}
                  sx={btnOutline(FP.color.navy)}
                >
                  Imprimir
                </Button>
              </Grid>
            )}
            {procesado && (
              <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary, fontSize: '0.85rem' }}>
                  {rows.length} préstamo{rows.length === 1 ? '' : 's'}
                </Typography>
              </Grid>
            )}
          </Grid>

          {procesado && rows.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: FP.color.textPrimary }}>
                {esCartera
                  ? 'Cartera General de Préstamos'
                  : `${resultado?.empleado?.numemp} - ${resultado?.empleado?.nombres}`}
              </Typography>
              <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: FP.color.navy }}>
                Balance Total Activo: {fmtMoney(resultado?.total_balance)}
              </Typography>
            </Box>
          )}

          <Box sx={{ bgcolor: '#fff' }}>
            <DataGrid
              autoHeight
              rows={rows}
              columns={esCartera ? columnsCartera : columnsIndividual}
              loading={cargando}
              rowHeight={42}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, '--DataGrid-overlayHeight': '220px' }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      <Box ref={tablaRef} sx={{ display: 'none' }}>
        {procesado && renderTablaImpresion()}
      </Box>
    </Box>
  );
};

export default EstadoPrestamos;
